import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Document } from "@/types"
import { auth } from "@/lib/firebase"

// Define the type for the delete function context if needed (optional for optimistic updates)
interface DeleteDocumentContext {
	previousDocuments: Document[] | undefined
}

export const useDeleteDocument = (caseId: string) => {
	const queryClient = useQueryClient()

	const mutation = useMutation<
		void, // Type returned by mutationFn
		Error, // Type of error
		string, // Type of variables passed to mutationFn (documentId)
		DeleteDocumentContext // Type of context (optional)
	>({
		mutationFn: async (documentId: string) => {
			const user = auth.currentUser
			if (!user) {
				throw new Error("User not found.")
			}
			const token = await user.getIdToken()
			// Access Vite env variables using import.meta.env
			const backendUrl = import.meta.env.VITE_API_URL
			if (!backendUrl) {
				throw new Error("VITE_API_URL environment variable not set.")
			}
			const response = await fetch(
				`${backendUrl}/api/cases/${caseId}/documents/${documentId}/`,
				{
					method: "DELETE",
					headers: { Authorization: `Bearer ${token}` },
				}
			)

			if (!response.ok) {
				const errorText = await response.text()
				throw new Error(
					`Failed to delete document: ${response.status} ${errorText}`
				)
			}
		},
		onSuccess: (data, documentId) => {
			// Invalidate all document queries for this case
			queryClient.invalidateQueries({
				queryKey: ["documents", caseId],
				exact: false, // This will match all queries that start with ["documents", caseId]
			})
			// Also invalidate the case query
			queryClient.invalidateQueries({
				queryKey: ["cases", caseId],
				exact: false,
			})
		},
		onError: (error) => {
			console.error("Error deleting document:", error.message)
			// Optionally: Add user notification here (e.g., using useToast)
		},
	})

	return mutation
}
